export type WithRequiredProps<T, K extends keyof T> = Required<Pick<T, K>> & T;

export type RequiredNonNull<T> = {
    [P in keyof T]-?: NonNullable<T[P]>;
};

export type WithRequiredPropsNonNull<T, K extends keyof T> = RequiredNonNull<Pick<T, K>> & T;

export function hasRequiredPropsNonNull<T, K extends keyof T>(
    obj: T,
    ...keysToCheck: K[]
): obj is WithRequiredPropsNonNull<T, K> {

    return obj !== null && obj !== undefined && keysToCheck.every(key => obj[key] !== null && obj[key] !== undefined);
}

export type RecursivePartial<T> = {
    [P in keyof T]?: T[P] extends Array<infer U>
        ? Array<RecursivePartial<U>>
        : T[P] extends Date
        ? T[P]
        : T[P] extends object
        ? RecursivePartial<T[P]>
        : T[P];
};

export type MappedRecursiveToType<T, V> = {
    [P in keyof T]-?: Exclude<T[P], undefined> extends unknown[] // If its an array set the array to the type.
        ? V
        : // If it is an extension of the type, set it the the type
        Exclude<T[P], undefined> extends V
        ? V
        : // If its an object, recursively set the object to the type

        Exclude<T[P], undefined> extends object
        ? MappedRecursiveToType<T[P], V>
        : // Otherwise it must be the type
          V;
};

function isMappedRecursiveToTypeOrString<T, V>(obj: any): obj is MappedRecursiveToType<T, V> {
    return typeof obj !== 'string';
}

export function convertRecursiveToDotNotation<T, V>(
    recursiveObject: MappedRecursiveToType<T, V>,
    dotChar = '.',
): Record<string, V> {
    const all: Record<string, V> = {};
    for (const key of Object.keys(recursiveObject)) {
        const value: MappedRecursiveToType<V, V> | V = recursiveObject[key as keyof typeof recursiveObject] as
            | MappedRecursiveToType<V, V>
            | V;
        if (isMappedRecursiveToTypeOrString<V, V>(value)) {
            const subValues = convertRecursiveToDotNotation(value, dotChar);
            for (const subKey of Object.keys(subValues)) {
                all[`${key}${dotChar}${subKey}`] = subValues[subKey];
            }
        } else {
            all[key] = value;
        }
    }
    return all;
}

// eslint-disable-next-line @typescript-eslint/array-type
export type GetArrayElementType<T extends any[]> = T extends (infer U)[] ? U : never;

export type RecursiveReadOnly<T> = {
    readonly [Key in keyof T]: RecursiveReadOnly<T[Key]>;
};

/**
 * Makes any property that allows for undefined types to be converted to an optional object
 *
 * type Example = {
 *    a: string;
 *    b: undefined;
 *    c?: string;
 *    d?: string | undefined;
 *    e: string | undefined;
 * };
 *
 * Will look like:
 *
 * ExampleObject {
 *    a: string;
 *    b?: undefined;
 *    c?: string | undefined;
 *    d?: string | undefined;
 *    e?: string | undefined;
 * }
 */
export type WithExcludedUndefinedFields<T> = {
    [K in keyof T as undefined extends T[K] ? never : K]: undefined extends T[K] ? never : T[K];
} & { [K in keyof T as undefined extends T[K] ? K : never]?: T[K] };
